import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";

type Testimonial = { quote: string; author: string; session: string; rating?: number };

export default function TestimonialCarousel({ testimonials }: { testimonials: Testimonial[] }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(next, 8000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [next, paused]);

  return (
    <section
      className="px-6 py-10 bg-section-alt"
      aria-label="Client testimonials"
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-3xl mx-auto">
        {/* Screen reader live region */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          Testimonial {active + 1} of {testimonials.length}: {testimonials[active].author}
        </div>

        <div className="relative h-[220px] flex items-center justify-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center"
              role="group"
              aria-roledescription="slide"
              aria-label={`${active + 1} of ${testimonials.length}`}
            >
              <div className="flex gap-1 mb-4 justify-center" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} size={9} className="fill-warm-gold text-warm-gold" />
                ))}
              </div>
              <blockquote>
                <p className="font-quote text-sm md:text-base leading-relaxed text-foreground mb-6 max-w-2xl">
                  "{testimonials[active].quote}"
                </p>
                <div className="w-8 h-px bg-warm-gold mx-auto mb-4" aria-hidden="true" />
                <footer>
                  <cite className="not-italic">
                    <p className="text-[9px] font-semibold tracking-[0.25em] uppercase text-foreground">
                      {testimonials[active].author}
                    </p>
                    {testimonials[active].session && (
                      <p className="text-[9px] tracking-[0.25em] uppercase text-warm-gold mt-1.5 font-light">
                        {testimonials[active].session}
                      </p>
                    )}
                  </cite>
                </footer>
              </blockquote>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
